import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../composants/Button/Button';
import Banner from '../../composants/Banner/Banner';
import { ReactComponent as LogoIcon } from "../../assets/logo/logo-centralis-gris.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/view-more.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/visibility-off.svg";
import CGVAccept from '../../composants/CGVAccept/CGVAccept';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import './Register.css';
import OrganismeAutocomplete from '../../composants/OrganismeAutocomplete/OrganismeAutocomplete';
import Switch from '../../composants/Switch/Switch';
// FRONT-17: Import du nouveau composant de sélection des lots
import LotsSelectorInscription, { LotDepartementSelection } from './components/LotsSelectorInscription';
import { departementFromCodePostal } from '../../utils/departement';
import { AdresseSaisie, ligneVoie, PAYS_EUROPE, PAYS_HORS_EUROPE } from '../../utils/adresse';
import ChampSiret from '../../composants/ChampSiret/ChampSiret';
import { StatutSirene, useVerificationSiret } from '../../composants/ChampSiret/useVerificationSiret';
import ChampAdresse from '../../composants/ChampAdresse/ChampAdresse';

interface Organisme {
  id: number;
  nom: string;
  siret?: string | null;
  departement?: string | null;
  // Admins de l'organisme (« Acheteur signataire admin »), deja formates « Prenom N. » par l'API :
  // la route est publique, le nom complet ne sort pas du serveur. Absent = API anterieure a ce champ.
  admins?: string[];
}

// Ajouter cette nouvelle interface au début du fichier avec les autres interfaces
interface Entite {
  id: number;
  nom: string;
}

// FRONT-17: Les interfaces LotTechnique et Departement ne sont plus nécessaires ici
// Elles sont gérées dans LotsSelectorInscription

const Register: React.FC = () => {

  const location = useLocation();
  const locationState = location.state as { step?: number; isAcheteur?: boolean } | null;


  // État pour gérer les étapes : 0 = cards, 1 = acheteur, 2 = prestataire
  const [currentStep, setCurrentStep] = useState<number>(locationState?.step ?? 0);
  const [isAcheteur, setIsAcheteur] = useState<null | boolean>(locationState?.isAcheteur ?? null);


  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    confirm_email: '',
    password: '',
    password_confirmation: '',
    organisme: '',
    organisme_id: null as number | null,
    organisme_departement: '' as string | null,
    organisme_nom_new: '',

    telephone: '',
    departement: '',
    code_postal: '',
    ville: '',
    prenom: '',
    acceptCGV: false,
    acceptDeliberation: false,
    numero_voie: '',
    type_voie: '',
    nom_voie: '',
    siret: '',
  });



  const [error, setError] = useState('');
  const [cgvError, setCgvError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Validite HTML5 du formulaire acheteur (checkValidity), recalculee a chaque changement : le
  // bouton « S'inscrire » reste grise tant que le formulaire est invalide.
  const [formulaireAcheteurValide, setFormulaireAcheteurValide] = useState(false);
  // Vrai apres une tentative de soumission (clic sur le bouton grise, refus du navigateur) :
  // les champs invalides prennent alors une bordure rouge, par CSS sur :invalid.
  const [tentativeSoumission, setTentativeSoumission] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [organismeExists, setOrganismeExists] = useState<boolean | null>(null);
  const [organismeOptions, setOrganismeOptions] = useState<Organisme[]>([]);
  const [entiteOptions, setEntiteOptions] = useState<Entite[]>([]); // État pour les options d'entité
  const [entiteExists, setEntiteExists] = useState<boolean | null>(null);
  const [isEntiteSearchLoading, setIsEntiteSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [entiteSearchTimeout, setEntiteSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [passwordValidation, setPasswordValidation] = useState({
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    isLongEnough: false,
    notEmail: true
  });
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [telError, setTelError] = useState('');
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successRedirectUrl, setSuccessRedirectUrl] = useState('/login');
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});


  const [isOrganismeSearchLoading, setIsOrganismeSearchLoading] = useState(false);
  const [siretPrestataire, setSiretPrestataire] = useState('');
  const [siretError, setSiretError] = useState('');
  const [siretExists, setSiretExists] = useState<boolean | null>(null);
  const [isSiretLoading, setIsSiretLoading] = useState(false);
  const verificationSiret = useVerificationSiret();
  // Retour SIRENE du dernier SIRET complet saisi ; un seul formulaire est affiche a la fois.
  const [statutSirene, setStatutSirene] = useState<StatutSirene | null>(null);
  const [entrepriseEnFrance, setEntrepriseEnFrance] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [prestataireSouscription, setPrestataireSouscription] = useState({
    entreprise: '',
    precision: '',
    // Code ISO 3166-1 alpha-2, pas un libelle : c'est ce qu'attend `entreprise.pays` cote serveur
    // (regle `size:2`). Renseigne des le choix France / autre pays a l'etape precedente.
    pays: 'FR',
    siret: siretPrestataire,
    rcs: '',
    statut_juridique: '',
    capital: '',
    date_creation: '',
    redressement_judiciaire: false,
    redressement: false,
    email: '',
    confirm_email: '',
    nom: '',
    prenom: '',
    fonction: '',
    telephone: '',
    accord_cadre: false,
    // Champs adresse
    code_postal: '',
    ville: '',
    numero_voie: '',
    type_voie: '',
    nom_voie: '',
    // Champs mot de passe
    password: '',
    password_confirmation: '',
    // Acceptation des CGV
    acceptCGV: false,
  });

  // Etat pour les erreurs de validation prestataire
  const [prestataireEmailError, setPrestataireEmailError] = useState('');
  const [prestataireTelError, setPrestataireTelError] = useState('');
  const [dateCreationError, setDateCreationError] = useState('');

  // Etats mot de passe prestataire
  const [prestatairePasswordValidation, setPrestatairePasswordValidation] = useState({
    hasLowerCase: false, hasUpperCase: false, hasNumber: false,
    hasSpecialChar: false, isLongEnough: false, notEmail: false,
  });
  const [showPrestatairePassword, setShowPrestatairePassword] = useState(false);
  const [showPrestatairePasswordConfirm, setShowPrestatairePasswordConfirm] = useState(false);

  // FRONT-17: État unifié pour les lots techniques et zones d'intervention
  // Remplace les anciens états selectedLotsTechniques et selectedDepartements
  const [selectedLotsDepartements, setSelectedLotsDepartements] = useState<LotDepartementSelection[]>([]);

  const organismeSelectedRef = useRef<boolean>(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const nomRef = useRef<HTMLInputElement>(null);
  const prenomRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const telephoneAcheteurRef = useRef<HTMLInputElement>(null);
  const organismeInputRef = useRef<HTMLInputElement>(null);
  const siretAcheteurRef = useRef<HTMLInputElement>(null);
  const siretPrestataireRef = useRef<HTMLInputElement>(null);
  // Refs champs prestataire
  const prestataireNomRef = useRef<HTMLInputElement>(null);
  const prestatairePrenomRef = useRef<HTMLInputElement>(null);
  const prestataireEmailRef = useRef<HTMLInputElement>(null);
  const prestataireTelRef = useRef<HTMLInputElement>(null);
  const prestatairePasswordRef = useRef<HTMLInputElement>(null);

  // Scroll en haut à chaque changement d'étape
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTentativeSoumission(false);
  }, [currentStep]);

  // FRONT-17: Le chargement des lots est maintenant géré par LotsSelectorInscription
  // Les anciens useEffect et handleDepartementToggle ont été supprimés

  // Effet pour rechercher l'organisme
  useEffect(() => {
    if (organismeSelectedRef.current) {
      organismeSelectedRef.current = false;
      return;
    }

    if (!formData.organisme || formData.organisme.length < 2) {
      setOrganismeOptions([]);
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeoutId = setTimeout(() => {
      searchOrganisme(formData.organisme);
    }, 500);

    setSearchTimeout(timeoutId);

    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [formData.organisme]);

  // Vérification de la correspondance des mots de passe
  useEffect(() => {
    setPasswordsMatch(
      formData.password === formData.password_confirmation &&
      formData.password !== ''
    );
  }, [formData.password, formData.password_confirmation]);


  const searchOrganisme = async (query: string) => {
    setIsOrganismeSearchLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/organismes/autocomplete/${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      const data = await response.json();

      if (data.status === 'success' && Array.isArray(data.data)) {
        setOrganismeOptions(data.data);
        setOrganismeExists(data.data.length > 0);
        if (data.data.length === 0) {
          // Pré-remplir le nom seulement si la recherche était textuelle (pas un SIRET)
          const queryValue = formData.organisme.trim();
          const isNumeric = /^\d+$/.test(queryValue);
          setFormData(prev => ({ ...prev, organisme_nom_new: isNumeric ? '' : queryValue }));
        }
      } else {
        setOrganismeOptions([]);
        setOrganismeExists(false);
        const queryValue = formData.organisme.trim();
        const isNumeric = /^\d+$/.test(queryValue);
        setFormData(prev => ({ ...prev, organisme_nom_new: isNumeric ? '' : queryValue }));
      }
    } catch (error) {
      console.error('Erreur de recherche organisme:', error);
      setOrganismeOptions([]);
      setOrganismeExists(false);
      const queryValue = formData.organisme.trim();
      const isNumeric = /^\d+$/.test(queryValue);
      setFormData(prev => ({ ...prev, organisme_nom_new: isNumeric ? '' : queryValue }));
    } finally {
      setIsOrganismeSearchLoading(false);
    }
  };

  // Ajouter cette fonction après la définition de searchOrganisme
  const searchEntite = async (query: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/entites/autocomplete/${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      const data = await response.json();

      if (data.status === 'success' && Array.isArray(data.data)) {
        // Traiter les suggestions d'entités ici
        const entites: Entite[] = data.data;
        // Mettre à jour l'état avec les suggestions d'entités
        setEntiteOptions(entites);
        setEntiteExists(entites.length > 0);
      } else {
        setEntiteOptions([]);
        setEntiteExists(false);
      }
    } catch (error) {
      console.error('Erreur de recherche entité:', error);
      setEntiteOptions([]);
      setEntiteExists(false);
    }
  };

  // Fonction de vérification du SIRET/numéro d'identification
  const verifySiret = async (siret: string) => {
    // Vérification du format selon le pays
    if (entrepriseEnFrance) {
      // France : SIRET de 14 chiffres
      if (siret.length !== 14 || !/^\d{14}$/.test(siret)) {
        setSiretError('Le SIRET doit contenir exactement 14 chiffres');
        setSiretExists(null);
        return;
      }
    } else {
      // Autres pays européens : numéro d'identification variable
      if (siret.length < 5 || siret.length > 20) {
        setSiretError('Le numéro d\'identification doit contenir entre 5 et 20 caractères');
        setSiretExists(null);
        return;
      }
    }

    setSiretError('');
    setIsSiretLoading(true);

    // Code pays pour l'API : FR pour France, EU pour autres pays européens
    const paysCode = entrepriseEnFrance ? 'FR' : 'EU';

    try {
      // Vérifier si le numéro d'identification existe déjà dans la base (organismes + entreprises)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/inscription/check-siret/${siret}?pays=${paysCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      const data = await response.json();

      if (data.status === 'success') {
        if (data.exists) {
          setSiretError(entrepriseEnFrance
            ? 'Ce SIRET est déjà enregistré sur Centralis. Veuillez vous connecter.'
            : 'Ce numéro d\'identification est déjà enregistré sur Centralis. Veuillez vous connecter.');
          setSiretExists(true);
        } else {
          setSiretExists(false);
          setSiretPrestataire(siret);
          setPrestataireSouscription(prevState => ({
            ...prevState,
            siret: siret
          }));
          // Passer à l'étape suivante
          setCurrentStep(3);
        }
      }
    } catch (error) {
      console.error('Erreur de vérification:', error);
      setSiretError(entrepriseEnFrance
        ? 'Erreur lors de la vérification du SIRET'
        : 'Erreur lors de la vérification du numéro d\'identification');
    } finally {
      setIsSiretLoading(false);
    }
  };

  // Un SIRET complet declenche le controle d'unicite et la recherche de l'adresse SIRENE. Une
  // reponse arrivee apres qu'un autre SIRET a ete saisi est ignoree par le hook (null).
  const controlerSiretAcheteur = async (siret: string) => {
    verificationSiret.annuler();
    setFormData(prev => ({ ...prev, siret }));
    setStatutSirene(null);
    setFieldErrors(prev => (prev.siret ? { ...prev, siret: '' } : prev));
    if (siret.length !== 14) return;

    const resultat = await verificationSiret.verifier(siret);
    if (!resultat) return;
    if (resultat.existe) {
      setFieldErrors(prev => ({ ...prev, siret: 'Ce SIRET est déjà enregistré sur Centralis. Vous ne pouvez pas recréer un organisme ou une entreprise avec le même SIRET.' }));
      return;
    }
    setStatutSirene(resultat.statutSirene);
    if (resultat.adresse) {
      const adresse = resultat.adresse;
      setFormData(prev => ({ ...prev, ...adresse, departement: departementFromCodePostal(adresse.code_postal) }));
    }
  };

  const controlerSiretPrestataire = async (siret: string) => {
    verificationSiret.annuler();
    setSiretPrestataire(siret);
    setSiretError('');
    setSiretExists(null);
    setStatutSirene(null);
    if (siret.length !== 14) return;

    const resultat = await verificationSiret.verifier(siret);
    if (!resultat) return;
    if (resultat.existe) {
      setSiretError('Ce SIRET est déjà enregistré sur Centralis. Veuillez vous connecter.');
      setSiretExists(true);
      return;
    }
    setStatutSirene(resultat.statutSirene);
    if (resultat.adresse) {
      const adresse = resultat.adresse;
      setPrestataireSouscription(prev => ({ ...prev, ...adresse }));
    }
  };

  // Identifiant d'une entreprise hors de France : alphanumerique, 5 a 20 caracteres, controle
  // au clic sur Continuer.
  const handleIdentifiantEtrangerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSiretPrestataire(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20).toUpperCase());
    setSiretError('');
    setSiretExists(null);
  };

  // Recalcule la validite du formulaire acheteur a chaque changement (frappe, selection
  // d'organisme, cases, adresse SIRENE).
  useEffect(() => {
    setFormulaireAcheteurValide(!!formRef.current?.checkValidity());
  }, [formData, organismeExists, isAcheteur, currentStep]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCgvError('');
    setIsLoading(true);

    try {
      // Validation initiale
      if (isAcheteur === null) {
        throw new Error("Veuillez répondre à la question 'Êtes-vous un acheteur public?'");
      }

      // Vérification CGV uniquement pour les acheteurs (les prestataires ont la checkbox dans leur formulaire)
      if (isAcheteur && !formData.acceptCGV) {
        // Ouvrir le modal des CGV au lieu de lancer une erreur
        setIsCGVModalOpen(true);
        setCgvError("Vous devez lire et accepter les Conditions Générales pour vous inscrire.");
        setIsLoading(false);
        return;
      }

      if (isAcheteur) {
        // Logique pour les acheteurs
        if (organismeExists === true) {
          // Cas organisme existant — appel atomique avec transaction backend
          if (!formData.organisme_id) {
            throw new Error("Veuillez sélectionner votre organisme dans la liste proposée.");
          }

          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register/acheteur`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email.toLowerCase(),
              password: formData.password,
              password_confirmation: formData.password_confirmation,
              nom: formData.nom,
              prenom: formData.prenom,
              telephone: formData.telephone,
              id_organisme: formData.organisme_id,
            }),
          });

          const data = await response.json();

          if (data.status !== 'success') {
            if (data.errors) {
              const flat: Record<string, string> = {};
              Object.entries(data.errors).forEach(([key, msgs]) => {
                if (Array.isArray(msgs) && msgs.length > 0) flat[key] = msgs[0];
              });
              setFieldErrors(flat);
              const firstKey = Object.keys(flat)[0];
              const refMap: Record<string, React.RefObject<HTMLInputElement>> = {
                email: emailRef, password: passwordRef, nom: nomRef,
                prenom: prenomRef, telephone: telephoneAcheteurRef, id_organisme: organismeInputRef,
              };
              refMap[firstKey]?.current?.focus();
              setIsLoading(false);
              return;
            }
            throw new Error(data.message || "Erreur lors de l'inscription");
          }

          setSuccessMessage("Votre compte a bien été créé. Un email de vérification vous a été envoyé à l'adresse " + formData.email.toLowerCase() + ". Cliquez sur le lien pour activer votre compte.");
          setSuccessRedirectUrl('/login');
          setIsSuccessPopupOpen(true);

        } else if (organismeExists === false) {
          // Cas nouvel organisme — appel atomique avec transaction backend

          // type_voie volontairement non bloquant : des adresses francaises valides
          // (esplanade, lieu-dit, cours...) n'ont pas de type de voie normalise et le
          // pre-remplissage SIRENE/IGN peut le laisser vide. nom_voie porte le libelle complet.
          if (!formData.nom_voie ||
            !formData.departement || !formData.code_postal || !formData.ville ||
            !formData.telephone || !formData.prenom) {
            throw new Error("Tous les champs sont obligatoires.");
          }

          // Vérification SIRET avant soumission — unique dans organismes + entreprises
          if (formData.siret && /^\d{14}$/.test(formData.siret)) {
            const siretCheckRes = await fetch(`${process.env.REACT_APP_API_URL}/inscription/check-siret/${formData.siret}`);
            const siretCheckJson = await siretCheckRes.json();
            if (siretCheckJson.status === 'success' && siretCheckJson.exists) {
              setFieldErrors(prev => ({ ...prev, siret: 'Ce SIRET est déjà enregistré sur Centralis. Vous ne pouvez pas recréer un organisme ou une entreprise avec le même SIRET.' }));
              siretAcheteurRef.current?.focus();
              setIsLoading(false);
              return;
            }
          }

          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register/responsable-achat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              email: formData.email.toLowerCase(),
              password: formData.password,
              password_confirmation: formData.password_confirmation,
              nom: formData.nom,
              prenom: formData.prenom,
              telephone: formData.telephone,
              organisme_nom: formData.organisme_nom_new || formData.organisme,
              siret: formData.siret || null,
              numero_voie: formData.numero_voie,
              type_voie: formData.type_voie,
              nom_voie: formData.nom_voie,
              code_postal: formData.code_postal,
              ville: formData.ville,
              departement: formData.departement,
            }),
          });

          const data = await response.json();

          if (data.status !== 'success') {
            if (data.errors) {
              const flat: Record<string, string> = {};
              Object.entries(data.errors).forEach(([key, msgs]) => {
                if (Array.isArray(msgs) && msgs.length > 0) flat[key] = msgs[0];
              });
              setFieldErrors(flat);
              const firstKey = Object.keys(flat)[0];
              const refMap: Record<string, React.RefObject<HTMLInputElement>> = {
                email: emailRef, password: passwordRef, nom: nomRef,
                prenom: prenomRef, telephone: telephoneAcheteurRef, organisme_nom: organismeInputRef,
                siret: siretAcheteurRef,
              };
              refMap[firstKey]?.current?.focus();
              setIsLoading(false);
              return;
            }
            throw new Error(data.message || "Erreur lors de l'inscription");
          }

          setSuccessMessage("Votre compte a bien été créé. Un email de vérification vous a été envoyé à l'adresse " + formData.email.toLowerCase() + ". Cliquez sur le lien pour activer votre compte.");
          setSuccessRedirectUrl('/login');
          setIsSuccessPopupOpen(true);
        } else {
          // organismeExists === null — aucun organisme recherché
          setFieldErrors(prev => ({ ...prev, id_organisme: 'Veuillez rechercher et sélectionner votre organisme.' }));
          organismeInputRef.current?.focus();
          return;
        }
      } else {
        // Logique pour les prestataires - Inscription complète via nouvelle API
        // Validations
        if (!prestataireSouscription.entreprise) {
          throw new Error("Le nom de l'entreprise est obligatoire.");
        }
        if (!prestataireSouscription.email) {
          throw new Error("L'email de l'administrateur est obligatoire.");
        }
        if (!prestataireSouscription.nom || !prestataireSouscription.prenom) {
          throw new Error("Le nom et prenom de l'administrateur sont obligatoires.");
        }
        if (prestataireSouscription.email !== prestataireSouscription.confirm_email) {
          throw new Error("Les adresses email ne correspondent pas.");
        }
        if (!prestataireSouscription.rcs) {
          throw new Error("Le numéro RCS est obligatoire.");
        }
        if (!prestataireSouscription.nom_voie || !prestataireSouscription.code_postal || !prestataireSouscription.ville) {
          throw new Error("L'adresse complète de l'entreprise est obligatoire.");
        }
        if (!prestataireSouscription.pays) {
          throw new Error("Le pays du siège de l'entreprise est obligatoire.");
        }
        if (entrepriseEnFrance && !/^\d{5}$/.test(prestataireSouscription.code_postal)) {
          throw new Error("Le code postal français doit contenir exactement 5 chiffres.");
        }
        // FRONT-17: Validation adaptée pour le nouveau format
        if (selectedLotsDepartements.length === 0) {
          throw new Error("Veuillez sélectionner au moins un lot technique et une zone d'intervention.");
        }
        // Vérifier qu'il y a au moins une zone par lot technique sélectionné
        const lotsTechniquesIds = Array.from(new Set(selectedLotsDepartements.map(ld => ld.lot_technique_id)));
        if (lotsTechniquesIds.length === 0) {
          throw new Error("Veuillez sélectionner au moins un lot technique.");
        }
        if (!prestataireSouscription.acceptCGV) {
          throw new Error("Vous devez accepter les Conditions Générales.");
        }

        // FRONT-17: Extraire les lots techniques et départements uniques depuis selectedLotsDepartements
        const uniqueLotsTechniques = Array.from(new Set(selectedLotsDepartements.map(ld => ld.lot_technique_id)));
        const uniqueDepartements = Array.from(new Set(selectedLotsDepartements.map(ld => ld.departement_id)));

        // Construire le payload selon le format attendu par RegisterPrestataireRequest
        const payload = {
          siret: siretPrestataire,
          entreprise: {
            nom: prestataireSouscription.entreprise,
            precision: prestataireSouscription.precision || null,  // FRONT-24: BUG FIX - Ajouter precision
            // Le pays reel, pas le pseudo-code 'EU' envoye jusqu'ici pour tout siege non francais :
            // il ne designait aucun pays et l'information etait definitivement perdue.
            pays: prestataireSouscription.pays,
            rcs: prestataireSouscription.rcs,
            statut_juridique: prestataireSouscription.statut_juridique || null,
            capital: prestataireSouscription.capital ? parseInt(prestataireSouscription.capital.replace(/\s/g, ''), 10) : null,
            date_creation: prestataireSouscription.date_creation ? `${prestataireSouscription.date_creation}-01-01` : null,
            redressement_judiciaire: prestataireSouscription.redressement || false,
            // `adresse` est la ligne de voie complete attendue par le serveur, recomposee depuis
            // les champs eclates : il la re-parse en dernier recours, et une valeur vide ferait
            // echouer la validation `required`.
            adresse: ligneVoie(prestataireSouscription.numero_voie, prestataireSouscription.type_voie, prestataireSouscription.nom_voie),
            code_postal: prestataireSouscription.code_postal,
            ville: prestataireSouscription.ville,
            numero_voie: prestataireSouscription.numero_voie,
            // Le type de voie est une notion de l'adressage francais (rue, avenue, boulevard).
            // Hors de France il reste vide, et la voie entiere tient dans `nom_voie`.
            type_voie: prestataireSouscription.type_voie,
            nom_voie: prestataireSouscription.nom_voie,
          },
          // FRONT-17: Utilise les valeurs extraites du nouveau format
          lots_techniques: uniqueLotsTechniques,
          departements: uniqueDepartements,
          // FRONT-17: Envoyer aussi le détail lot-département pour l'API
          lots_departements: selectedLotsDepartements.map(ld => ({
            lot_technique_id: ld.lot_technique_id,
            departement_id: ld.departement_id,
          })),
          administrateur: {
            nom: prestataireSouscription.nom,
            prenom: prestataireSouscription.prenom,
            email: prestataireSouscription.email.toLowerCase(),
            telephone: prestataireSouscription.telephone,
            password: prestataireSouscription.password,
            password_confirmation: prestataireSouscription.password_confirmation,
          },
          interesse_accord_cadre: prestataireSouscription.accord_cadre || false,
          accepte_cgu: true,
          accepte_confidentialite: true,
        };

        // Vérification finale SIRET avant soumission (protection contre race condition)
        const paysCode = entrepriseEnFrance ? 'FR' : 'EU';
        const siretCheckResponse = await fetch(`${process.env.REACT_APP_API_URL}/inscription/check-siret/${siretPrestataire}?pays=${paysCode}`);
        const siretCheckData = await siretCheckResponse.json();
        if (siretCheckData.status === 'success' && siretCheckData.exists) {
          throw new Error(entrepriseEnFrance
            ? 'Ce SIRET est déjà enregistré sur Centralis. Veuillez vous connecter.'
            : 'Ce numéro d\'identification est déjà enregistré sur Centralis. Veuillez vous connecter.');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register/prestataire`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.status === 409) {
          // SIRET déjà existant
          throw new Error(data.message || 'Ce SIRET est déjà enregistré.');
        }

        if (response.status === 422) {
          const errors = data.errors;
          if (errors) {
            const flat: Record<string, string> = {};
            Object.entries(errors).forEach(([key, msgs]) => {
              // Les erreurs de la personne s'aplatissent sur ses champs ; cote entreprise seuls les
              // champs d'adresse sont rendus par ChampAdresse, et `entreprise.nom` ne doit pas
              // ecraser `administrateur.nom`.
              const fieldKey = key
                .replace(/^administrateur\./, '')
                .replace(/^entreprise\.(?=(numero_voie|nom_voie|code_postal|ville)$)/, '');
              if (Array.isArray(msgs) && (msgs as string[]).length > 0) flat[fieldKey] = (msgs as string[])[0];
            });
            setFieldErrors(flat);
            const firstKey = Object.keys(flat)[0];
            const refMap: Record<string, React.RefObject<HTMLInputElement>> = {
              email: prestataireEmailRef,
              password: prestatairePasswordRef,
              nom: prestataireNomRef,
              prenom: prestatairePrenomRef,
              telephone: prestataireTelRef,
            };
            refMap[firstKey]?.current?.focus();
            setIsLoading(false);
            return;
          }
          throw new Error(data.message || 'Erreur de validation.');
        }

        if (data.status !== 'success') {
          throw new Error(data.message || 'Une erreur est survenue lors de l\'inscription.');
        }

        // Inscription réussie - Afficher la popup de confirmation
        // FRONT-18: Message clair sur obligation de vérifier l'email + redirection vers /login
        setSuccessMessage("Votre compte a bien été créé. Un email de vérification vous a été envoyé à votre adresse. Cliquez sur le lien reçu pour activer votre compte.");
        setSuccessRedirectUrl('/login');
        setIsSuccessPopupOpen(true);
      }
    } catch (error) {
      console.error('Register error:', error);
      const msg = error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'inscription';
      setError(msg);
      setErrorPopupMessage(msg);
      setIsErrorPopupOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour vérifier le mot de passe en direct
  const validatePassword = (password: string) => {
    setPasswordValidation({
      hasLowerCase: /[a-z]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      isLongEnough: password.length >= 8,
      notEmail: !/\S+@\S+\.\S+/.test(password) // Vérifie que le mot de passe n'est pas une adresse email
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let newValue = value;
    // For telephone field, remove non-digit characters and limit length to 10
    if (name === 'telephone') {
      newValue = value.replace(/\D/g, '');
      if (newValue.length > 10) {
        newValue = newValue.slice(0, 10);
      }
    }

    // Mise à jour de tous les champs du formulaire y compris acceptCGV
    setFormData(prev => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : newValue
    }));

    // Effacer l'erreur inline du champ modifié
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Valider le mot de passe en direct
    if (name === 'password') {
      validatePassword(value);
    }
  };

  // Ouvre un document légal d'inscription par son ID en base (table document_commun) :
  // 4 = exemple de délibération, 3 = trame d'habilitation. Recherche par ID car les
  // noms en base ne sont pas fiables. Endpoint public core25 (pas de token : /register
  // est pré-connexion). Échec silencieux : la consultation ne bloque pas l'inscription.
  const openCandidatureDoc = async (docId: number) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/inscription/documents-communs/${docId}`,
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      // Pas de blocage de l'inscription si la consultation échoue.
    }
  };

  // Le champ `pays` est un select : sans elargir le type, l'evenement d'un <select> n'est pas
  // assignable a ChangeEvent<HTMLInputElement> et la compilation echoue.
  const handlePrestataireSouscriptionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setPrestataireSouscription(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur inline du champ modifié
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectOrganisme = (org: { id: number; nom: string; siret?: string | null; departement?: string | null }) => {
    organismeSelectedRef.current = true;
    setFormData(prev => ({ ...prev, organisme: org.nom, organisme_id: org.id, organisme_departement: org.departement ?? null }));
    setOrganismeOptions([]);
    setOrganismeExists(true);
  };

  const handleSelectEntite = (entiteNom: string) => {
    setPrestataireSouscription(prev => ({ ...prev, entite: entiteNom }));
    setEntiteOptions([]);
    setEntiteExists(true);
  };

  // Ajouter cette fonction au début du composant Register
  const fetchWithoutAuth = async (url: string, options: RequestInit = {}) => {
    // Assurez-vous que les en-têtes Authorization ne sont pas inclus
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };

    // Filtrer explicitement pour supprimer Authorization s'il existe
    delete (headers as any)['Authorization'];

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const handleTelephoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const tel = formData.telephone;
    if (tel && !/^\d{10}$/.test(tel)) {
      setTelError("Le numéro de téléphone doit comporter 10 chiffres.");
    } else {
      setTelError('');
    }
  };

  // Vérification de la correspondance des emails
  useEffect(() => {
    if (formData.confirm_email !== '' && formData.email !== formData.confirm_email) {
      setEmailError("Les adresses email ne correspondent pas.");
    } else {
      setEmailError('');
    }
  }, [formData.email, formData.confirm_email]);

  // Fonction pour obtenir les titres selon l'étape
  const getTitles = () => {
    switch (currentStep) {
      case 0:
        return {
          title: 'Inscription',
          subtitle: 'Choisissez votre type de compte'
        };
      case 1:
        // Étape 1 : Formulaire acheteur
        return {
          title: 'Inscription Acheteur',
          subtitle: 'Complétez les informations suivantes'
        };
      case 2:
        // Étape 2 : Vérification SIRET/numéro d'identification prestataire
        if (entrepriseEnFrance === null) {
          return {
            title: 'Localisation de l\'entreprise',
            subtitle: 'Indiquez où est située votre entreprise'
          };
        }
        return {
          title: entrepriseEnFrance ? 'Vérification du SIRET' : 'Vérification du numéro d\'identification',
          subtitle: entrepriseEnFrance
            ? 'Veuillez saisir votre numéro SIRET pour commencer votre inscription'
            : 'Veuillez saisir votre numéro d\'identification d\'entreprise'
        };
      case 3:
        // Étape 3 : Formulaire prestataire
        return {
          title: 'Inscription Prestataire',
          subtitle: 'Complétez les informations suivantes'
        };
      case 4:
        // Étape 4 : Confirmation prestataire
        return {
          title: 'Inscription réussie',
        }
      default:
        return {
          title: 'Inscription',
          subtitle: 'Choisissez votre type de compte'
        };
    }
  };

  const { title, subtitle } = getTitles();

  const adresseOrganisme: AdresseSaisie = {
    numero_voie: formData.numero_voie,
    type_voie: formData.type_voie,
    nom_voie: formData.nom_voie,
    code_postal: formData.code_postal,
    ville: formData.ville,
  };
  const adressePrestataire: AdresseSaisie = {
    numero_voie: prestataireSouscription.numero_voie,
    type_voie: prestataireSouscription.type_voie,
    nom_voie: prestataireSouscription.nom_voie,
    code_postal: prestataireSouscription.code_postal,
    ville: prestataireSouscription.ville,
  };

  // Un bouton desactive n'emet aucun clic ; avec `pointer-events: none` (Register.css) le clic
  // passe au span qui l'entoure, qui montre a l'utilisateur le premier champ en defaut
  // (defilement, focus, bulle du navigateur), les champs invalides passant en rouge.
  const expliquerBlocageAcheteur = () => {
    setTentativeSoumission(true);
    formRef.current?.reportValidity();
  };
  const continuerSiretGrise = isSiretLoading
    || (entrepriseEnFrance ? siretPrestataire.length !== 14 : siretPrestataire.length < 5);
  const expliquerBlocageSiret = () => {
    setTentativeSoumission(true);
    siretPrestataireRef.current?.focus();
  };

  return (
    <div className="register-container">
      <Link to="/" className="register-back-home-link">← Retour à l&apos;accueil</Link>
      <div className="register-card">
        <div className="logo-container">
          <LogoIcon className="logo" />
        </div>
        <h1>{title}</h1>

        <h2>{subtitle}</h2>



        {/* Question Acheteur */}
        {/* <div className="form-group si-acheteur">
          <label>Êtes-vous un acheteur public ?<span style={{ color: 'red' }}>*</span></label>
          <div className="bouton-radio">
            <label>
              <input
                type="radio"
                name="isAcheteur"
                checked={isAcheteur === true}
                onChange={() => setIsAcheteur(true)}
                required
              />
              Oui
            </label>
            <label>
              <input
                type="radio"
                name="isAcheteur"
                checked={isAcheteur === false}
                onChange={() => setIsAcheteur(false)}
                required
              />
              Non
            </label>
          </div>
        </div> */}

        {/* {isAcheteur === false && (
          <div className="prestataire-section">
            <div className="info-message">
              <p>Vous ne pouvez pas vous inscrire si vous n'êtes pas un acheteur.</p>
              <p>Si vous êtes prestataire, les candidatures se font tous les deux ans. Pour être tenu au courant de notre prochaine phase de candidature, renseignez votre email :</p>
            </div>
            <form onSubmit={handleSubmit} className="prestataire-form">
              <div className="form-group">
                <label htmlFor="entite">Nom de l'entité<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  id="entite"
                  name="entite"
                  value={prestataireSouscription.entite}
                  onChange={handlePrestataireSouscriptionChange}
                  placeholder="Nom de votre entreprise"
                  required
                  autoComplete="off"
                  className="organisme-input"
                />
                <div className="icon-container">
                  {isEntiteSearchLoading ? (
                    <div className="search-loading"></div>
                  ) : (
                    <SearchIcon className="search-icon" />
                  )}
                </div>
                {entiteOptions.length > 0 && (
                  <ul className="autocomplete-list">
                    {entiteOptions.map(ent => (
                      <li key={ent.id} onClick={() => handleSelectEntite(ent.nom)}>
                        {ent.nom}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="email"
                  id="email-prestataire"
                  name="email"
                  value={prestataireSouscription.email}
                  onChange={handlePrestataireSouscriptionChange}
                  placeholder="exemple@entreprise.com"
                  required
                />
              </div>

              <div className="form-group cgv-container">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="acceptCGV"
                    checked={formData.acceptCGV}
                    onChange={handleChange}
                    required
                  />
                  <span>J'accepte les <a href="/cgv" target="_blank" rel="noopener noreferrer">Conditions Générales de Vente</a></span>
                </label>
              </div>

              <div className="form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  value={isLoading ? "Souscription en cours..." : "S'inscrire"}
                  size="medium"
                  disabled={isLoading}
                />
              </div>
            </form>
          </div>
        )} */}
        {/* Deux cards (une acheteur et une prestataire ui ouvre le bon formulaire en fonction du clic) */}

        {currentStep === 0 && (
          <div className="account-type-cards">
            <div
              className={`account-type-card ${isAcheteur === true ? 'selected' : ''}`}
              onClick={() => {
                setIsAcheteur(true);
                setCurrentStep(1);
              }}
            >
              <div className="prestataire-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#D97947" fill-opacity="0.1" />

                  <g transform="translate(1.5, 2)">
                    <path d="M46.6667 38.3333L50.0001 41.6667L56.6667 35" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M46.6666 55V51.6667C46.6666 49.8986 45.9642 48.2029 44.714 46.9526C43.4637 45.7024 41.768 45 39.9999 45H29.9999C28.2318 45 26.5361 45.7024 25.2859 46.9526C24.0356 48.2029 23.3333 49.8986 23.3333 51.6667V55" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M34.9999 38.3333C38.6818 38.3333 41.6666 35.3486 41.6666 31.6667C41.6666 27.9848 38.6818 25 34.9999 25C31.318 25 28.3333 27.9848 28.3333 31.6667C28.3333 35.3486 31.318 38.3333 34.9999 38.3333Z" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                </svg>

              </div>
              <h3>Acheteur public</h3>
              <p>Accédez à l'annuaire des prestataires qualifiés et gérez vos commandes</p>
              <Button
                type="button"
                variant="primary"
                value="S'inscrire comme acheteur"
                size="small"
                onClick={() => {
                  setIsAcheteur(true);
                  setCurrentStep(1);
                }}
              />
            </div>
            <div
              className={`account-type-card ${isAcheteur === false ? 'selected' : ''}`}
              onClick={() => {
                setIsAcheteur(false);
                setCurrentStep(2);
              }}
            >
              {/* Icone */}
              <div className="prestataire-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#D97947" fill-opacity="0.1" />

                  <g transform="translate(-1.5, -1.5)">
                    <path d="M36.6666 40H43.3333" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M36.6666 33.3333H43.3333" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M43.3333 55V50C43.3333 49.1159 42.9821 48.2681 42.357 47.643C41.7319 47.0178 40.884 46.6667 40 46.6667C39.1159 46.6667 38.2681 47.0178 37.6429 47.643C37.0178 48.2681 36.6666 49.1159 36.6666 50V55" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M30 36.6667H26.6667C25.7827 36.6667 24.9348 37.0178 24.3097 37.643C23.6846 38.2681 23.3334 39.1159 23.3334 40V51.6667C23.3334 52.5507 23.6846 53.3986 24.3097 54.0237C24.9348 54.6488 25.7827 55 26.6667 55H53.3334C54.2174 55 55.0653 54.6488 55.6904 54.0237C56.3155 53.3986 56.6667 52.5507 56.6667 51.6667V35C56.6667 34.1159 56.3155 33.2681 55.6904 32.643C55.0653 32.0178 54.2174 31.6667 53.3334 31.6667H50" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M30 55V28.3333C30 27.4493 30.3512 26.6014 30.9763 25.9763C31.6014 25.3512 32.4493 25 33.3333 25H46.6667C47.5507 25 48.3986 25.3512 49.0237 25.9763C49.6488 26.6014 50 27.4493 50 28.3333V55" stroke="#D97947" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                </svg>

              </div>
              <h3>Prestataire</h3>
              <p>Rejoigner Centralis et accédez aux marchés publics</p>
              <Button
                type="button"
                variant="primary"
                value="S'inscrire comme prestataire"
                size="small"
                onClick={() => {
                  setIsAcheteur(false);
                  setCurrentStep(2);
                }}
              />
            </div>
          </div>
        )}

        {/* Étape 2 : Vérification SIRET prestataire */}
        {currentStep === 2 && isAcheteur === false && (
          <div className="siret-verification-card">
            <div className="siret-card-content">
              <div className="siret-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#D97947" fillOpacity="0.1" />
                  <g transform="translate(-1.5, -1.5)">
                    <path d="M36.6666 40H43.3333" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M36.6666 33.3333H43.3333" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M43.3333 55V50C43.3333 49.1159 42.9821 48.2681 42.357 47.643C41.7319 47.0178 40.884 46.6667 40 46.6667C39.1159 46.6667 38.2681 47.0178 37.6429 47.643C37.0178 48.2681 36.6666 49.1159 36.6666 50V55" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M30 36.6667H26.6667C25.7827 36.6667 24.9348 37.0178 24.3097 37.643C23.6846 38.2681 23.3334 39.1159 23.3334 40V51.6667C23.3334 52.5507 23.6846 53.3986 24.3097 54.0237C24.9348 54.6488 25.7827 55 26.6667 55H53.3334C54.2174 55 55.0653 54.6488 55.6904 54.0237C56.3155 53.3986 56.6667 52.5507 56.6667 51.6667V35C56.6667 34.1159 56.3155 33.2681 55.6904 32.643C55.0653 32.0178 54.2174 31.6667 53.3334 31.6667H50" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M30 55V28.3333C30 27.4493 30.3512 26.6014 30.9763 25.9763C31.6014 25.3512 32.4493 25 33.3333 25H46.6667C47.5507 25 48.3986 25.3512 49.0237 25.9763C49.6488 26.6014 50 27.4493 50 28.3333V55" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                </svg>
              </div>

              {/* Choix France ou UE */}
              {entrepriseEnFrance === null ? (
                <div className="pays-question-section">
                  <div className="form-group">
                    <label>Votre entreprise est-elle située en France ?<span style={{ color: 'red' }}>*</span></label>
                    <div className="pays-choice-buttons">
                      <button
                        type="button"
                        className="pays-choice-btn"
                        onClick={() => {
                          setEntrepriseEnFrance(true);
                          setPrestataireSouscription(prev => ({ ...prev, pays: 'FR' }));
                        }}
                      >
                        <span className="flag-emoji">🇫🇷</span>
                        Oui, en France
                      </button>
                      <button
                        type="button"
                        className="pays-choice-btn"
                        onClick={() => {
                          setEntrepriseEnFrance(false);
                          // Vide le pays pour forcer le choix explicite a l'etape suivante : garder
                          // 'FR' ferait passer une entreprise etrangere pour francaise, avec le
                          // gabarit de code postal francais impose derriere.
                          setPrestataireSouscription(prev => ({ ...prev, pays: '' }));
                        }}
                      >
                        <span className="flag-emoji">🇪🇺</span>
                        Non, autre pays européen
                      </button>
                    </div>
                  </div>
                  <div className="siret-card-actions">
                    <Button
                      type="button"
                      className='back-button no-scale-button'
                      variant="secondary"
                      value="Retour"
                      size="small"
                      onClick={() => {
                        setCurrentStep(0);
                        setIsAcheteur(null);
                      }}
                    />
                  </div>
                </div>
              ) : (
                /* Saisie du numéro d'identification */
                <div className={`siret-input-section${tentativeSoumission ? ' formulaire-tentative' : ''}`}>
                  {entrepriseEnFrance ? (
                    <ChampSiret
                      id="siret-prestataire"
                      label="Numéro SIRET"
                      placeholder="12345678901234"
                      valeur={siretPrestataire}
                      onChange={controlerSiretPrestataire}
                      erreur={siretError}
                      chargement={verificationSiret.chargement}
                      statutSirene={statutSirene}
                      inputRef={siretPrestataireRef}
                    />
                  ) : (
                    <div className="form-group">
                      <label htmlFor="siret-prestataire">Numéro d'identification européen<span style={{ color: 'red' }}>*</span></label>
                      <input
                        ref={siretPrestataireRef}
                        type="text"
                        id="siret-prestataire"
                        name="siret"
                        value={siretPrestataire}
                        onChange={handleIdentifiantEtrangerChange}
                        placeholder="Ex: BE0123456789"
                        className={siretError ? 'input-error' : ''}
                        maxLength={20}
                        minLength={5}
                        required
                      />
                      <div className="siret-counter">{siretPrestataire.length} caractères (5 à 20 max)</div>
                      <div className="siret-info-text">
                        Saisissez le numéro d'identification de votre entreprise (TVA intracommunautaire, numéro d'entreprise, etc.)
                      </div>
                      {siretError && <span className="error-message">{siretError}</span>}
                    </div>
                  )}
                  {siretExists === false && (
                    <span className="success-message">✓ Numéro valide et disponible</span>
                  )}

                  <div className="siret-card-actions">
                    <Button
                      type="button"
                      className='back-button no-scale-button'
                      variant="secondary"
                      value="Retour"
                      size="small"
                      onClick={() => {
                        setEntrepriseEnFrance(null);
                        setSiretPrestataire('');
                        setSiretError('');
                        setSiretExists(null);
                        setStatutSirene(null);
                        verificationSiret.annuler();
                        setPrestataireSouscription(prev => ({ ...prev, pays: 'FR' }));
                      }}
                    />
                    <span
                      className="bouton-grise-cliquable"
                      onClick={continuerSiretGrise ? expliquerBlocageSiret : undefined}
                    >
                      <Button
                        type="button"
                        className='siret-check-button no-scale-button'
                        variant="primary"
                        value={isSiretLoading ? 'Vérification...' : 'Continuer'}
                        size="small"
                        onClick={() => verifySiret(siretPrestataire)}
                        disabled={continuerSiretGrise}
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Étape 3 : Complétez les informations suivantes prestataire */}
        {currentStep === 3 && isAcheteur === false && (
          <form onSubmit={handleSubmit} className={tentativeSoumission ? 'formulaire-tentative' : undefined}>
            <div className="prestataire-section">
              <div className="form-header-container">
                <div className="siret-icon">
                  <svg width="60" height="60" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#D97947" fillOpacity="0.1" />
                    <g transform="translate(-1.5, -1.5)">
                      <path d="M36.6666 40H43.3333" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M36.6666 33.3333H43.3333" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M43.3333 55V50C43.3333 49.1159 42.9821 48.2681 42.357 47.643C41.7319 47.0178 40.884 46.6667 40 46.6667C39.1159 46.6667 38.2681 47.0178 37.6429 47.643C37.0178 48.2681 36.6666 49.1159 36.6666 50V55" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M30 36.6667H26.6667C25.7827 36.6667 24.9348 37.0178 24.3097 37.643C23.6846 38.2681 23.3334 39.1159 23.3334 40V51.6667C23.3334 52.5507 23.6846 53.3986 24.3097 54.0237C24.9348 54.6488 25.7827 55 26.6667 55H53.3334C54.2174 55 55.0653 54.6488 55.6904 54.0237C56.3155 53.3986 56.6667 52.5507 56.6667 51.6667V35C56.6667 34.1159 56.3155 33.2681 55.6904 32.643C55.0653 32.0178 54.2174 31.6667 53.3334 31.6667H50" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M30 55V28.3333C30 27.4493 30.3512 26.6014 30.9763 25.9763C31.6014 25.3512 32.4493 25 33.3333 25H46.6667C47.5507 25 48.3986 25.3512 49.0237 25.9763C49.6488 26.6014 50 27.4493 50 28.3333V55" stroke="#D97947" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                  </svg>
                </div>
                <div className='form-header'>
                  <h4>Informations entreprise</h4>
                  <p>Informations légales de votre entreprise</p>
                </div>
              </div>
              <div className="prestataire-form">

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="entreprise">Nom de l'entreprise<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="entreprise"
                      name="entreprise"
                      value={prestataireSouscription.entreprise}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Nom de votre entreprise"
                      maxLength={150}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="precision">Précision<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="precision"
                      name="precision"
                      value={prestataireSouscription.precision}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Ex: (Agence Alpes Côte d'Azur)"
                      maxLength={100}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="pays">Pays<span style={{ color: 'red' }}>*</span></label>
                    {/* Ce champ etait une saisie libre jamais transmise : le payload envoyait 'FR'
                        ou 'EU' selon le choix de l'etape precedente, et le pays reel saisi ici etait
                        perdu. Il porte desormais un code ISO qui part vraiment en base, verrouille
                        sur la France quand l'entreprise y est declaree. */}
                    <select
                      id="pays"
                      name="pays"
                      value={prestataireSouscription.pays}
                      onChange={handlePrestataireSouscriptionChange}
                      disabled={entrepriseEnFrance === true}
                      className={entrepriseEnFrance === true ? 'input-disabled' : ''}
                      required
                    >
                      <option value="">Sélectionnez...</option>
                      <optgroup label="Europe">
                        {PAYS_EUROPE.map(p => (
                          <option key={p.code} value={p.code}>{p.nom}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Reste du monde">
                        {PAYS_HORS_EUROPE.map(p => (
                          <option key={p.code} value={p.code}>{p.nom}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="siret">SIRET</label>
                    <input
                      type="text"
                      id="siret"
                      name="siret"
                      value={prestataireSouscription.siret}
                      disabled // Bloqué car vérifié à l'étape précédente
                      className="input-disabled"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="rcs">RCS<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="rcs"
                      name="rcs"
                      value={prestataireSouscription.rcs}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Ex: RCS Paris 123 456 789"
                      maxLength={50}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="statut_juridique">Statut juridique<span style={{ color: 'red' }}>*</span></label>
                    <select
                      id="statut_juridique"
                      name="statut_juridique"
                      value={prestataireSouscription.statut_juridique}
                      onChange={(e: any) => handlePrestataireSouscriptionChange(e)}
                      required
                    >
                      <option value="">Sélectionnez...</option>
                      <option value="SAS">SAS</option>
                      <option value="SARL">SARL</option>
                      <option value="EURL">EURL</option>
                      <option value="SA">SA</option>
                      <option value="Auto-entrepreneur">Auto-entrepreneur</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="capital">Capital (€)<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="capital"
                      name="capital"
                      value={prestataireSouscription.capital}
                      onChange={(e) => {
                        // Capital entier : chiffres uniquement, formatage des milliers FR.
                        const rawValue = e.target.value.replace(/\D/g, '');
                        const formatted = rawValue === '' ? '' : new Intl.NumberFormat('fr-FR').format(Number(rawValue));
                        setPrestataireSouscription(prev => ({ ...prev, capital: formatted }));
                      }}
                      placeholder="Ex: 10 000"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="date_creation">Créée depuis (Année)<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="number"
                      id="date_creation"
                      name="date_creation"
                      min={1800}
                      max={new Date().getFullYear()}
                      value={prestataireSouscription.date_creation}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Limiter a 4 chiffres maximum
                        if (value.length <= 4) {
                          setPrestataireSouscription(prev => ({ ...prev, date_creation: value }));
                          // Effacer l'erreur pendant la saisie
                          if (dateCreationError) setDateCreationError('');
                        }
                      }}
                      onBlur={(e) => {
                        const year = parseInt(e.target.value, 10);
                        const currentYear = new Date().getFullYear();
                        if (e.target.value && (isNaN(year) || year < 1800 || year > currentYear)) {
                          setDateCreationError('Veuillez saisir une date valide');
                          setPrestataireSouscription(prev => ({ ...prev, date_creation: '' }));
                        } else {
                          setDateCreationError('');
                        }
                      }}
                      placeholder="Ex: 1980"
                      required
                      className={dateCreationError ? 'input-error' : ''}
                    />
                    {dateCreationError && <span className="error-message">{dateCreationError}</span>}
                  </div>
                </div>

                <div className="form-group redressement-switch redressement-section">
                  <div className="redressement-cadre-label-block">
                    <label htmlFor='redressement-switch' className="redressement-cadre-label">
                      Redressement judiciaire
                    </label>
                    <div className="redressement-cadre-description">
                      L'entreprise est-elle en redressement ?
                    </div>
                  </div>
                  {/* Redressement judiciaire switch */}
                  <div className="redressement-cadre-switch-control">
                    <Switch
                      id="redressement-switch"
                      isOn={!!prestataireSouscription.redressement}
                      handleToggle={e => setPrestataireSouscription(prev => ({ ...prev, redressement: e.target.checked }))}
                      onColor="#f97316"
                    />
                  </div>
                </div>

                {/* Adresse de l'entreprise : recherche IGN en France, champs eclates partout. */}
                <ChampAdresse
                  idPrefixe="entreprise-adresse"
                  enFrance={entrepriseEnFrance === true}
                  valeur={adressePrestataire}
                  onChange={(adresse) => setPrestataireSouscription(prev => ({ ...prev, ...adresse }))}
                  erreurs={fieldErrors}
                />



              </div>
            </div>

            <div className="prestataire-section">
              <div className="form-header-container">
                <div className="siret-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#D97947" fill-opacity="0.1" />
                    <path d="M26.6667 18.3334C26.6667 22.4942 22.0508 26.8275 20.5008 28.1659C20.3564 28.2744 20.1807 28.3331 20 28.3331C19.8193 28.3331 19.6436 28.2744 19.4992 28.1659C17.9492 26.8275 13.3333 22.4942 13.3333 18.3334C13.3333 16.5652 14.0357 14.8696 15.2859 13.6193C16.5362 12.3691 18.2319 11.6667 20 11.6667C21.7681 11.6667 23.4638 12.3691 24.714 13.6193C25.9643 14.8696 26.6667 16.5652 26.6667 18.3334Z" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M20 20.8333C21.3807 20.8333 22.5 19.714 22.5 18.3333C22.5 16.9526 21.3807 15.8333 20 15.8333C18.6193 15.8333 17.5 16.9526 17.5 18.3333C17.5 19.714 18.6193 20.8333 20 20.8333Z" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>

                </div>
                <div className='form-header'>
                  <h4>Référencement dans Centralis</h4>
                  <p>Sélectionnez vos domaines d'intervention</p>
                </div>
              </div>

              {/* FRONT-17: Nouveau composant de sélection des lots avec distinction régional/départemental */}
              <div className="prestataire-form">
                <LotsSelectorInscription
                  selectedLots={selectedLotsDepartements}
                  onChange={setSelectedLotsDepartements}
                  disabled={false}
                />
              </div>

            </div>

            <div className="prestataire-section">
              <div className="form-header-container">
                <div className="siret-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#D97947" fill-opacity="0.1" />
                    <path d="M25.8333 27.5V25.8333C25.8333 24.9493 25.4821 24.1014 24.857 23.4763C24.2319 22.8512 23.3841 22.5 22.5 22.5H17.5C16.616 22.5 15.7681 22.8512 15.143 23.4763C14.5179 24.1014 14.1667 24.9493 14.1667 25.8333V27.5" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M20 19.1667C21.841 19.1667 23.3333 17.6743 23.3333 15.8333C23.3333 13.9924 21.841 12.5 20 12.5C18.1591 12.5 16.6667 13.9924 16.6667 15.8333C16.6667 17.6743 18.1591 19.1667 20 19.1667Z" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>


                </div>
                <div className='form-header'>
                  <h4>Administrateur du compte</h4>
                  <p>Personne responsable du compte</p>
                </div>
              </div>

              <div className="prestataire-form">

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="nom">Nom<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      ref={prestataireNomRef}
                      value={prestataireSouscription.nom}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Nom"
                      maxLength={80}
                      required
                      className={fieldErrors.nom ? 'input-error' : ''}
                    />
                    {fieldErrors.nom && <span className="error-message">{fieldErrors.nom}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="prenom">Prénom<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      ref={prestatairePrenomRef}
                      value={prestataireSouscription.prenom}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Prénom"
                      maxLength={80}
                      required
                      className={fieldErrors.prenom ? 'input-error' : ''}
                    />
                    {fieldErrors.prenom && <span className="error-message">{fieldErrors.prenom}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fonction">Fonction<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="fonction"
                      name="fonction"
                      value={prestataireSouscription.fonction}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Ex: Directeur General"
                      maxLength={100}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="telephone">Téléphone<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      ref={prestataireTelRef}
                      value={prestataireSouscription.telephone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPrestataireSouscription(prev => ({ ...prev, telephone: value }));
                        if (prestataireTelError) setPrestataireTelError('');
                        if (fieldErrors.telephone) setFieldErrors(prev => ({ ...prev, telephone: '' }));
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && !/^\d{10}$/.test(value)) {
                          setPrestataireTelError('Le numéro de téléphone doit comporter 10 chiffres.');
                        } else {
                          setPrestataireTelError('');
                        }
                      }}
                      placeholder="Ex: 0612345678"
                      required
                      className={(prestataireTelError || fieldErrors.telephone) ? 'input-error' : ''}
                    />
                    {(prestataireTelError || fieldErrors.telephone) && <span className="error-message">{fieldErrors.telephone || prestataireTelError}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      ref={prestataireEmailRef}
                      value={prestataireSouscription.email}
                      onChange={(e) => {
                        handlePrestataireSouscriptionChange(e);
                        // Valider la correspondance des emails si confirm_email est rempli
                        if (prestataireSouscription.confirm_email && e.target.value !== prestataireSouscription.confirm_email) {
                          setPrestataireEmailError('Les adresses email ne correspondent pas.');
                        } else {
                          setPrestataireEmailError('');
                        }
                      }}
                      placeholder="email@entreprise.fr"
                      maxLength={254}
                      required
                      className={fieldErrors.email ? 'input-error' : ''}
                    />
                    {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirm_email_prestataire">Confirmer l'email<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="email"
                      id="confirm_email_prestataire"
                      name="confirm_email"
                      value={prestataireSouscription.confirm_email}
                      onChange={(e) => {
                        setPrestataireSouscription(prev => ({ ...prev, confirm_email: e.target.value }));
                        // Valider la correspondance des emails
                        if (prestataireSouscription.email !== e.target.value) {
                          setPrestataireEmailError('Les adresses email ne correspondent pas.');
                        } else {
                          setPrestataireEmailError('');
                        }
                      }}
                      placeholder="Confirmez votre email"
                      maxLength={254}
                      required
                      className={prestataireEmailError ? 'input-error' : ''}
                    />
                    {prestataireEmailError && <span className="error-message">{prestataireEmailError}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="prestataire_password">Mot de passe<span style={{ color: 'red' }}>*</span></label>
                  <div className="password-input-container">
                    <input
                      type={showPrestatairePassword ? "text" : "password"}
                      id="prestataire_password"
                      name="password"
                      ref={prestatairePasswordRef}
                      value={prestataireSouscription.password}
                      onChange={(e) => {
                        handlePrestataireSouscriptionChange(e);
                        setPrestatairePasswordValidation({
                          hasLowerCase: /[a-z]/.test(e.target.value),
                          hasUpperCase: /[A-Z]/.test(e.target.value),
                          hasNumber: /\d/.test(e.target.value),
                          hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(e.target.value),
                          isLongEnough: e.target.value.length >= 8,
                          notEmail: !/\S+@\S+\.\S+/.test(e.target.value),
                        });
                      }}
                      placeholder="Entrez votre mot de passe"
                      required
                      className={fieldErrors.password ? 'input-error' : ''}
                    />
                    {fieldErrors.password && <span className="error-message">{fieldErrors.password}</span>}
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowPrestatairePassword(!showPrestatairePassword)}
                    >
                      {showPrestatairePassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  <div className="password-requirements">
                    <p>Le mot de passe doit contenir :</p>
                    <ul>
                      <li className={prestatairePasswordValidation.hasLowerCase ? 'valid' : 'invalid'}>Une minuscule</li>
                      <li className={prestatairePasswordValidation.hasUpperCase ? 'valid' : 'invalid'}>Une majuscule</li>
                      <li className={prestatairePasswordValidation.hasNumber ? 'valid' : 'invalid'}>Un chiffre</li>
                      <li className={prestatairePasswordValidation.hasSpecialChar ? 'valid' : 'invalid'}>Un caractère spécial</li>
                      <li className={prestatairePasswordValidation.isLongEnough ? 'valid' : 'invalid'}>Au moins 8 caractères</li>
                      <li className={prestatairePasswordValidation.notEmail ? 'valid' : 'invalid'}>Ne doit pas être une adresse email</li>
                    </ul>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="prestataire_password_confirmation">Confirmer le mot de passe<span style={{ color: 'red' }}>*</span></label>
                  <div className="password-input-container">
                    <input
                      type={showPrestatairePasswordConfirm ? "text" : "password"}
                      id="prestataire_password_confirmation"
                      name="password_confirmation"
                      value={prestataireSouscription.password_confirmation}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="Confirmez votre mot de passe"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowPrestatairePasswordConfirm(!showPrestatairePasswordConfirm)}
                    >
                      {showPrestatairePasswordConfirm ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {prestataireSouscription.password_confirmation !== '' &&
                    prestataireSouscription.password !== prestataireSouscription.password_confirmation && (
                    <div className="error-message">Les mots de passe ne correspondent pas.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Section Intérêt Accord-Cadre */}
            {/* <div className="prestataire-section">
              <div className="form-header-container">
                <div className="siret-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#D97947" fill-opacity="0.1" />
                    <path d="M26.6667 13.3334H13.3333C12.4128 13.3334 11.6667 14.0795 11.6667 15V25C11.6667 25.9205 12.4128 26.6667 13.3333 26.6667H26.6667C27.5871 26.6667 28.3333 25.9205 28.3333 25V15C28.3333 14.0795 27.5871 13.3334 26.6667 13.3334Z" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M20 23.3333C21.841 23.3333 23.3333 21.841 23.3333 20C23.3333 18.1591 21.841 16.6667 20 16.6667C18.1591 16.6667 16.6667 18.1591 16.6667 20C16.6667 21.841 18.1591 23.3333 20 23.3333Z" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M15 13.3334V11.6667" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M25 13.3334V11.6667" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <div className='form-header'>
                  <h4>Intérêt Accord-Cadre</h4>
                  <p>Souhaitez-vous être informé de la prochaine session ?</p>
                </div>
              </div>

              <div className="prestataire-form">
                <div className="form-group accord-cadre-switch accord-cadre-section">
                  <div className="accord-cadre-label-block">
                    <label htmlFor='accord-cadre-switch' className="accord-cadre-label">
                      Intéressé par l'accord-cadre Centralis
                    </label>
                    <div className="accord-cadre-description">
                      L'accord-cadre vous permet de bénéficier de marchés publics facilités. 
                      Prochaine session d'attribution prévue prochainement.
                    </div>
                  </div>
                  <div className="accord-cadre-switch-control">
                    <Switch
                      id="accord-cadre-switch"
                      isOn={!!prestataireSouscription.accord_cadre}
                      handleToggle={e => setPrestataireSouscription(prev => ({ ...prev, accord_cadre: e.target.checked }))}
                      onColor="#f97316"
                    />
                  </div>
                </div>
              </div>
            </div> */}

            <div className="prestataire-section">
              <div className="form-header-container">
                <div className="siret-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#D97947" fill-opacity="0.1" />
                    <path d="M15 28.3333C14.558 28.3333 14.134 28.1577 13.8215 27.8451C13.5089 27.5326 13.3333 27.1087 13.3333 26.6666V13.3333C13.3333 12.8913 13.5089 12.4673 13.8215 12.1548C14.134 11.8422 14.558 11.6666 15 11.6666H21.6667C21.9305 11.6662 22.1917 11.718 22.4354 11.8189C22.6791 11.9199 22.9005 12.0681 23.0867 12.255L26.0767 15.245C26.264 15.4312 26.4126 15.6527 26.5139 15.8968C26.6152 16.1408 26.6671 16.4024 26.6667 16.6666V26.6666C26.6667 27.1087 26.4911 27.5326 26.1785 27.8451C25.8659 28.1577 25.442 28.3333 25 28.3333H15Z" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M21.6667 11.6666V15.8333C21.6667 16.0543 21.7545 16.2663 21.9107 16.4225C22.067 16.5788 22.279 16.6666 22.5 16.6666H26.6667" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M17.5 22.5L19.1667 24.1667L22.5 20.8334" stroke="#D97947" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>


                </div>
                <div className='form-header'>
                  <h4>Conditions Générales</h4>
                  <p>Acceptez les conditions d'utilisation</p>
                </div>
              </div>

              <div className="prestataire-form">

                <div className="form-group cgv-container">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptCGV"
                      checked={!!prestataireSouscription.acceptCGV}
                      onChange={(e) => setPrestataireSouscription(prev => ({ ...prev, acceptCGV: e.target.checked }))}
                      required
                    />
                    <span>J'accepte les <a href="/cgv" target="_blank" rel="noopener noreferrer">Conditions Générales</a><span style={{ color: 'red' }}>*</span></span>
                  </label>
                </div>

              </div>
            </div>

            <div className="form-actions-container">
              <Button
                type="button"
                variant="secondary"
                value="Retour"
                size="small"
                onClick={() => setCurrentStep(2)}
                className='back-button no-scale-button'
              />
              <Button
                type="submit"
                variant="primary"
                value={isLoading ? "Chargement..." : "Créer mon compte prestataire"}
                size="small"
                disabled={isLoading}
                onClick={() => setTentativeSoumission(true)}
                className='informations-entreprise-button no-scale-button'
              />
            </div>
          </form>
        )}

        {/* Étape 4 : Validation d'inscription prestataire */}
        {currentStep === 4 && isAcheteur === false && (
          <div className="registration-success-card">
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#DCFCE7" />
                <path d="M40 56.6667C49.2048 56.6667 56.6667 49.2048 56.6667 40C56.6667 30.7953 49.2048 23.3333 40 23.3333C30.7953 23.3333 23.3334 30.7953 23.3334 40C23.3334 49.2048 30.7953 56.6667 40 56.6667Z" stroke="#00A63E" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M35 40L38.3333 43.3333L45 36.6667" stroke="#00A63E" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

            </div>
            <h3>Votre compte prestataire a été créé</h3>
            <p>
              Vous devez compléter votre profil avant de pouvoir utiliser Centralis.
              Veuillez renseigner toutes les informations obligatoires pour activer votre compte.
            </p>
            <Button
              type="button"
              variant="primary"
              value="Accéder à mon profil"
              size="small"
              onClick={() => window.location.href = '/profil'}
              className='profile-button no-scale-button'
            />
          </div>
        )}

        {/*   {isAcheteur === false && (
          <div className="prestataire-section">

            <form onSubmit={handleSubmit} className="prestataire-form">
              <div className="form-group">
                <label htmlFor="entite">Nom de l'entité<span style={{ color: 'red' }}>*</span></label>
                <div className="autocomplete-container">
                  <input
                    type="text"
                    id="entite"
                    name="entite"
                    value={prestataireSouscription.entite}
                    onChange={handlePrestataireSouscriptionChange}
                    placeholder="Nom de votre entreprise"
                    required
                    autoComplete="off"
                    className="organisme-input"
                  />
                  <div className="icon-container">
                    {isEntiteSearchLoading ? (
                      <div className="search-loading"></div>
                    ) : (
                      <SearchIcon className="search-icon" />
                    )}
                  </div>
                  {entiteOptions.length > 0 && (
                    <ul className="autocomplete-list">
                      {entiteOptions.map(ent => (
                        <li key={ent.id} onClick={() => handleSelectEntite(ent.nom)}>
                          {ent.nom}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="utilisateur-section">
                <h3>Informations du contact</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="nom-prestataire">Nom<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="nom-prestataire"
                      name="nom"
                      value={prestataireSouscription.nom || ''}
                      onChange={(e) => setPrestataireSouscription(prev => ({ ...prev, nom: e.target.value }))}
                      placeholder="Votre nom"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="prenom-prestataire">Prénom<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="prenom-prestataire"
                      name="prenom"
                      value={prestataireSouscription.prenom || ''}
                      onChange={(e) => setPrestataireSouscription(prev => ({ ...prev, prenom: e.target.value }))}
                      placeholder="Votre prénom"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email-prestataire">Email<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="email"
                      id="email-prestataire"
                      name="email"
                      value={prestataireSouscription.email}
                      onChange={handlePrestataireSouscriptionChange}
                      placeholder="exemple@entreprise.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirm_email-prestataire">Confirmer l'email<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="email"
                      id="confirm_email-prestataire"
                      name="confirm_email"
                      value={prestataireSouscription.confirm_email || ''}
                      onChange={(e) => setPrestataireSouscription(prev => ({ ...prev, confirm_email: e.target.value }))}
                      placeholder="Confirmez votre email"
                      required
                      className={prestataireSouscription.email !== prestataireSouscription.confirm_email && prestataireSouscription.confirm_email ? 'input-error' : ''}
                    />
                    {prestataireSouscription.email !== prestataireSouscription.confirm_email && prestataireSouscription.confirm_email &&
                      <span className="error-message">Les adresses email ne correspondent pas.</span>
                    }
                  </div>
                  <div className="form-group">
                    <label htmlFor="telephone-prestataire">Téléphone<span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="tel"
                      id="telephone-prestataire"
                      name="telephone"
                      value={prestataireSouscription.telephone || ''}
                      onChange={(e) => {
                        const newValue = e.target.value.replace(/\D/g, '');
                        if (newValue.length <= 10) {
                          setPrestataireSouscription(prev => ({ ...prev, telephone: newValue }))
                        }
                      }}
                      placeholder="06 23 00 00 00"
                      required
                    />
                  </div>
                </div>
              </div>

              {cgvError && <div className="cgv-error">{cgvError}</div>}

              <div className="form-actions-container">
                <button
                  type="button"
                  className="cgv-view-button"
                  onClick={() => setIsCGVModalOpen(true)}
                >
                  Accepter les CGV
                </button>

                <Button
                  type="submit"
                  variant="primary"
                  value={isLoading ? "Souscription en cours..." : "S'inscrire"}
                  size="medium"
                  disabled={isLoading || !formData.acceptCGV}
                  className="submit-button"
                />
              </div>
            </form>
          </div>
        )} */}

        {/* Formulaire pour les acheteurs */}
        {currentStep === 1 && isAcheteur === true && (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`acheteur-form acheteur-section${tentativeSoumission ? ' formulaire-tentative' : ''}`}
          >
            {/* Organisme existant : une consigne, pas une erreur -> banniere info grise du design system
                (l'ancien cadre rouge maison se lisait comme un blocage avant meme la premiere saisie). */}
            <Banner
              variant="info"
              message={<>Si vous souhaitez rejoindre un organisme déjà existant sur Centralis, veuillez vérifier que l'organisme sélectionné dans le champ «&nbsp;Organisme&nbsp;» est correct. Nous vous invitons également à contacter l'administrateur de votre organisme afin qu'il puisse créer lui-même votre compte.</>}
            />

            {/* Choix de l'organisme */}
            <div className="form-group">
              <label htmlFor="organisme">
                Organisme<span style={{ color: 'red' }}>*</span>
              </label>
              <div className="autocomplete-container">
                <input
                  ref={organismeInputRef}
                  type="text"
                  id="organisme"
                  name="organisme"
                  value={formData.organisme}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, organisme: e.target.value, organisme_id: null }));
                    if (fieldErrors.id_organisme || fieldErrors.organisme_nom) {
                      setFieldErrors(prev => ({ ...prev, id_organisme: '', organisme_nom: '' }));
                    }
                  }}
                  placeholder="Nom ou SIRET de votre organisme"
                  required
                  maxLength={100}
                  autoComplete="off"
                  className={`organisme-input${fieldErrors.id_organisme || fieldErrors.organisme_nom ? ' input-error' : ''}`}
                />
                <div className="icon-container">
                  {isOrganismeSearchLoading ? (
                    <div className="search-loading"></div>
                  ) : (
                    <SearchIcon className="search-icon" />
                  )}
                </div>
                {organismeOptions.length > 0 && (
                  <ul className="autocomplete-list">
                    {organismeOptions.map(org => (
                      <li key={org.id} onClick={() => handleSelectOrganisme(org)}>
                        <div>
                          <span>{org.nom}</span>
                          {org.departement && (
                            <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>— {org.departement}</span>
                          )}
                        </div>
                        {/* A qui s'adresser pour etre rattache : la consigne du haut du formulaire renvoie vers
                            l'administrateur de l'organisme. Rien si l'API ne fournit pas le champ (deploiement
                            front avant core) : afficher « aucun » serait alors une fausse information. */}
                        {org.admins && (
                          <div className="organisme-admins">
                            {org.admins.length > 0
                              ? <>Administrateur{org.admins.length > 1 ? 's' : ''}&nbsp;: {org.admins.join(', ')}</>
                              : 'Aucun administrateur enregistré'}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {(fieldErrors.id_organisme || fieldErrors.organisme_nom) && (
                <span className="error-message">{fieldErrors.id_organisme || fieldErrors.organisme_nom}</span>
              )}
            </div>

            {/* <OrganismeAutocomplete
              value={formData.organisme}
              onChange={(val) => setFormData({ ...formData, organisme: val })}
              onSelect={(org) => setFormData({ ...formData, organisme: org.nom })}
              organismeOptions={organismeOptions}
              isLoading={isOrganismeSearchLoading}
            /> */}

            {/* Champs supplémentaires si l'organisme n'existe pas */}
            {organismeExists === false && (
              <div className="nouveau-organisme-section">
                <h3>Informations sur l'organisme</h3>

                <div className="form-group">
                  <label htmlFor="organisme_nom_new">Nom de l'organisme<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    id="organisme_nom_new"
                    name="organisme_nom_new"
                    value={formData.organisme_nom_new}
                    onChange={handleChange}
                    placeholder="Ex : Mairie de Paris"
                    maxLength={100}
                    required
                    className={fieldErrors.organisme_nom_new ? 'input-error' : ''}
                  />
                  {fieldErrors.organisme_nom_new && <span className="error-message">{fieldErrors.organisme_nom_new}</span>}
                </div>

                <ChampSiret
                  id="siret"
                  valeur={formData.siret}
                  onChange={controlerSiretAcheteur}
                  erreur={fieldErrors.siret}
                  chargement={verificationSiret.chargement}
                  statutSirene={statutSirene}
                  inputRef={siretAcheteurRef}
                />
                <ChampAdresse
                  idPrefixe="organisme-adresse"
                  enFrance
                  valeur={adresseOrganisme}
                  onChange={(adresse) => setFormData(prev => ({ ...prev, ...adresse, departement: departementFromCodePostal(adresse.code_postal) }))}
                  erreurs={fieldErrors}
                />

              </div>
            )}

            {/* Informations de l'utilisateur (titre modifié en fonction de l'existence de l'organisme) */}
            <div className="utilisateur-section">
              <h3>{organismeExists === true ? "Informations de l'utilisateur" : "Informations de l'administrateur du compte"}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nom">Nom<span style={{ color: 'red' }}>*</span></label>
                  <input
                    ref={nomRef}
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    required
                    className={fieldErrors.nom ? 'input-error' : ''}
                  />
                  {fieldErrors.nom && <span className="error-message">{fieldErrors.nom}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="prenom">Prénom<span style={{ color: 'red' }}>*</span></label>
                  <input
                    ref={prenomRef}
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder="Votre prénom"
                    required
                    className={fieldErrors.prenom ? 'input-error' : ''}
                  />
                  {fieldErrors.prenom && <span className="error-message">{fieldErrors.prenom}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email<span style={{ color: 'red' }}>*</span></label>
                  <input
                    ref={emailRef}
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="exemple@email.com"
                    required
                    className={fieldErrors.email ? 'input-error' : ''}
                  />
                  {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
                </div>
                {/* Champ de confirmation d'email */}
                <div className="form-group">
                  <label htmlFor="confirm_email">Confirmer l'email<span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="email"
                    id="confirm_email"
                    name="confirm_email"
                    value={formData.confirm_email}
                    onChange={handleChange}
                    placeholder="Confirmez votre email"
                    required
                    className={emailError ? 'input-error' : ''}
                  />
                  {emailError && <span className="error-message">{emailError}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="telephone">Téléphone<span style={{ color: 'red' }}>*</span></label>
                  <input
                    ref={telephoneAcheteurRef}
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 23 00 00 00"
                    required
                    onBlur={handleTelephoneBlur}
                    className={telError || fieldErrors.telephone ? 'input-error' : ''}
                  />
                  {(telError || fieldErrors.telephone) && <span className="error-message">{fieldErrors.telephone || telError}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Mot de passe<span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    ref={passwordRef}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Entrez votre mot de passe"
                    required
                    minLength={6}
                    className={fieldErrors.password ? 'input-error' : ''}
                  />
                  {fieldErrors.password && <span className="error-message">{fieldErrors.password}</span>}
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                <div className="password-requirements">
                  <p>Le mot de passe doit contenir :</p>
                  <ul>
                    <li className={passwordValidation.hasLowerCase ? 'valid' : 'invalid'}>Une minuscule</li>
                    <li className={passwordValidation.hasUpperCase ? 'valid' : 'invalid'}>Une majuscule</li>
                    <li className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>Un chiffre</li>
                    <li className={passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}>Un caractère spécial</li>
                    <li className={passwordValidation.isLongEnough ? 'valid' : 'invalid'}>Au moins 8 caractères</li>
                    <li className={passwordValidation.notEmail ? 'valid' : 'invalid'}>Ne doit pas être une adresse email</li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password_confirmation">Confirmer le mot de passe<span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    placeholder="Confirmez votre mot de passe"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                {!passwordsMatch && formData.password_confirmation !== '' && (
                  <div className="error-message">
                    Les mots de passe ne correspondent pas.
                  </div>
                )}
              </div>
            </div>

            {cgvError && <div className="cgv-error">{cgvError}</div>}

            <div className="form-group cgv-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="acceptCGV"
                  checked={!!formData.acceptCGV}
                  onChange={handleChange}
                  required
                />
                <span>J'accepte les <a href="/cgv" target="_blank" rel="noopener noreferrer">Conditions Générales</a><span style={{ color: 'red' }}>*</span></span>
              </label>
              <label className="checkbox-label align-top">
                <input
                  type="checkbox"
                  name="acceptDeliberation"
                  checked={!!formData.acceptDeliberation}
                  onChange={handleChange}
                  required
                />
                <span>
                  Je suis informé que certains acheteurs sont tenus de procéder à une délibération
                  obligatoire préalable afin d'utiliser Centralis et habiliter un administrateur qui
                  sera autorisé à engager l'organisme. Voici un exemple de{' '}
                  <a
                    href="#"
                    rel="noopener noreferrer"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCandidatureDoc(4); }}
                  >
                    délibération
                  </a>
                  {' '}et de{' '}
                  <a
                    href="#"
                    rel="noopener noreferrer"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCandidatureDoc(3); }}
                  >
                    trame d'habilitation
                  </a>
                  {' '}à charger signés dans votre profil après l'inscription.
                  <span style={{ color: 'red' }}>*</span>
                </span>
              </label>
            </div>

            <div className="form-actions-container">
              {/* Back button */}

              <Button
                type="button"
                className='back-button no-scale-button'
                variant="secondary"
                value="Retour"
                size="small"
                onClick={() => {
                  setCurrentStep(0);
                  setIsAcheteur(null);
                  setOrganismeExists(null);
                  setFormData(prev => ({
                    ...prev,
                    organisme: '',
                    organisme_nom_new: '',
                    siret: '',
                    departement: '',
                    code_postal: '',
                    ville: '',
                    numero_voie: '',
                    type_voie: '',
                    nom_voie: '',
                  }));
                  setStatutSirene(null);
                  verificationSiret.annuler();
                }}
              />

              <span
                className="bouton-grise-cliquable"
                onClick={!isLoading && !formulaireAcheteurValide ? expliquerBlocageAcheteur : undefined}
              >
                <Button
                  type="submit"
                  variant="primary"
                  value={isLoading ? "Inscription en cours..." : "S'inscrire"}
                  size="small"
                  disabled={isLoading || !formulaireAcheteurValide}
                  className="submit-button"
                />
              </span>
            </div>
          </form>
        )}

        {/* Modal d'affichage des CGV */}
        <CGVAccept
          acceptCGV={formData.acceptCGV}
          setAcceptCGV={(newAccept: boolean) =>
            setFormData(prev => ({ ...prev, acceptCGV: newAccept }))
          }
          isOpen={isCGVModalOpen}
          onClose={() => setIsCGVModalOpen(false)}
          onConfirm={() => {
            // Lorsque l'utilisateur confirme l'acceptation des CGV
            setFormData(prev => ({ ...prev, acceptCGV: true }));
            setIsCGVModalOpen(false);
          }}
          className="cgv-modal-large"
        />

        {/* Popup d'erreur d'inscription */}
        <ValidationPopUp
          isOpen={isErrorPopupOpen}
          title="Erreur lors de l'inscription"
          text={errorPopupMessage}
          onClose={() => setIsErrorPopupOpen(false)}
          onConfirm={() => setIsErrorPopupOpen(false)}
          confirmLabel="Fermer"
        />

        {/* Popup de succès d'inscription */}
        <ValidationPopUp
          isOpen={isSuccessPopupOpen}
          title="Inscription réussie !"
          text={successMessage}
          icon={
            <svg width="64" height="64" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="#DCFCE7" />
              <circle cx="40" cy="40" r="16.667" stroke="#00A63E" strokeWidth="3.333" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M35 40L38.333 43.333L45 36.667" stroke="#00A63E" strokeWidth="3.333" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
          onClose={() => {
            setIsSuccessPopupOpen(false);
            navigate(successRedirectUrl);
          }}
          onConfirm={() => {
            setIsSuccessPopupOpen(false);
            navigate(successRedirectUrl);
          }}
          confirmLabel="Compris"
        />

        <div className="register-footer">
          <p className="login-link">
            Déjà inscrit ? <Link to="/login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div >
  );
};

export default Register;